import React, { useMemo, useState } from 'react';
import { CandidateProduct, pickPreviewImage, productTypeLabel } from '../utils/premiumBundleRules';
import ShopMediaPreview from './ShopMediaPreview';

type Props = {
  candidates: CandidateProduct[];
  loading?: boolean;
  /** Keys shown as picked (checkbox state in multi mode, highlight in single mode). */
  selectedKeys: Set<string>;
  onPick: (candidate: CandidateProduct) => void;
  multi?: boolean;
  emptyText?: string;
  maxHeight?: number;
};

/** Searchable list of existing products: thumbnail, name, key, type and active status. */
const ProductPicker: React.FC<Props> = ({ candidates, loading, selectedKeys, onPick, multi, emptyText, maxHeight = 320 }) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return candidates.filter((candidate) => {
      if (status === 'active' && candidate.IsActive === false) return false;
      if (status === 'inactive' && candidate.IsActive !== false) return false;
      if (!q) return true;
      return (candidate.Name || '').toLowerCase().includes(q) || candidate.ProductKey.toLowerCase().includes(q);
    });
  }, [candidates, query, status]);

  return (
    <div className='space-y-2'>
      <div className='flex gap-2'>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search name or key...'
          className='flex-1 px-3 py-1.5 text-xs bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]'
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'all' | 'active' | 'inactive')}
          className='px-2 py-1.5 text-xs bg-black/40 border border-white/10 rounded-lg text-white'
        >
          <option value='all' className='bg-[#1E1E2D]'>All</option>
          <option value='active' className='bg-[#1E1E2D]'>Active</option>
          <option value='inactive' className='bg-[#1E1E2D]'>Inactive</option>
        </select>
      </div>
      <div className='overflow-y-auto space-y-1 pr-1' style={{ maxHeight }}>
        {loading ? (
          <p className='text-xs text-gray-400 py-2'>Loading products...</p>
        ) : visible.length === 0 ? (
          <p className='text-xs text-gray-500 py-2'>{candidates.length ? 'No products match.' : emptyText || 'No products of this type.'}</p>
        ) : (
          visible.map((candidate) => {
            const isSelected = selectedKeys.has(candidate.ProductKey);
            return (
              <button
                type='button'
                key={candidate.ProductKey}
                onClick={() => onPick(candidate)}
                className={`w-full flex items-center gap-3 text-left px-2 py-1.5 rounded-lg border transition ${
                  isSelected ? 'border-[#FF9C15]/70 bg-[#FF9C15]/10' : 'border-white/10 bg-white/5 hover:border-white/30'
                }`}
              >
                {multi && <input type='checkbox' readOnly checked={isSelected} className='w-4 h-4 accent-[#FF9C15] pointer-events-none' />}
                {/* Still only, to keep long lists light — unless there is no still (e.g. a profile
                    animation with only webm/mov), then play the video so the row isn't blank. */}
                <ShopMediaPreview assets={candidate.MediaAssets} size={40} showVideo={!pickPreviewImage(candidate.MediaAssets)} />
                <div className='min-w-0 flex-1'>
                  <div className='text-xs font-semibold text-white truncate'>{candidate.Name || candidate.ProductKey}</div>
                  <div className='text-[11px] text-gray-400 font-mono truncate'>{candidate.ProductKey}</div>
                </div>
                <div className='flex flex-col items-end gap-1 shrink-0'>
                  <span className='text-[10px] text-gray-400'>{productTypeLabel(candidate.Type)}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                      candidate.IsActive === false
                        ? 'text-red-300 border-red-400/40 bg-red-500/10'
                        : 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                    }`}
                  >
                    {candidate.IsActive === false ? 'Inactive' : 'Active'}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ProductPicker;
